import { useContext, useEffect, useState, useRef } from "react";
import { MyContext } from "../../Context/MyProvider";
import {
    useIsConnected,
    useJoin,
    useLocalMicrophoneTrack,
    usePublish,
    useRemoteUsers,
    useRemoteAudioTracks,
    RemoteAudioTrack,
} from "agora-rtc-react";
import { socket } from "../../socket";
import secureLocalStorage from "react-secure-storage";
import { useTranslation } from "react-i18next";

// ==========================================
// 1. Agora Audio Calling Sub-component
// ==========================================
const AgoraAudioCall = ({ CallData, agoraAppId, token, channel, micOn }) => {
    const { t } = useTranslation();
    const isConnected = useIsConnected();
    const remoteUsers = useRemoteUsers();
    const { localMicrophoneTrack } = useLocalMicrophoneTrack(micOn);

    usePublish([localMicrophoneTrack]);
    useJoin({ appid: agoraAppId, channel, token }, !!channel);

    const { audioTracks } = useRemoteAudioTracks(remoteUsers);

    useEffect(() => {
        return () => {
            if (localMicrophoneTrack) {
                localMicrophoneTrack.stop();
                localMicrophoneTrack.close();
            }
        };
    }, [localMicrophoneTrack]);

    return (
        <div className="flex flex-col items-center">
            <p className="text-gray-400 text-xs mt-2">
                {isConnected ? t("Connected (Agora Engine)") : t("Connecting to Agora...")}
            </p>
            <div style={{ width: 0, height: 0, opacity: 0, overflow: 'hidden', position: 'absolute' }}>
                {audioTracks.map((track) => (
                    <RemoteAudioTrack key={track.getUserId()} play track={track} />
                ))}
            </div>
        </div>
    );
};

// ==========================================
// 2. Native WebRTC Audio Sub-component
// ==========================================
const WebRtcAudioCall = ({ CallData, credentials, micOn }) => {
    const { t } = useTranslation();
    const [status, setStatus] = useState("Connecting P2P...");
    const localStreamRef = useRef(null);
    const peerConnectionRef = useRef(null);
    const remoteAudioRef = useRef(null);

    useEffect(() => {
        let isUnmounted = false;
        const appoint_id = CallData?.appoint_id;
        const currentUserId = secureLocalStorage.getItem("Userid");
        const stunUrl = credentials?.stunUrl || "stun:stun.l.google.com:19302";
        const turnUrl = credentials?.turnUrl;
        const turnUsername = credentials?.turnUsername;
        const turnPassword = credentials?.turnPassword;

        const iceServers = [{ urls: stunUrl }];
        if (turnUrl) {
            iceServers.push({
                urls: turnUrl,
                username: turnUsername,
                credential: turnPassword
            });
        }

        const pc = new RTCPeerConnection({ iceServers });
        peerConnectionRef.current = pc;

        // Get local audio stream
        navigator.mediaDevices.getUserMedia({ audio: true, video: false })
            .then((stream) => {
                if (isUnmounted) {
                    stream.getTracks().forEach(track => track.stop());
                    return;
                }
                localStreamRef.current = stream;
                // Mute/unmute based on micOn state
                stream.getAudioTracks().forEach(track => {
                    track.enabled = micOn;
                });

                stream.getTracks().forEach((track) => {
                    pc.addTrack(track, stream);
                });

                // User initiates the connection
                const isUser = String(CallData?.user_id) === String(currentUserId);
                if (isUser) {
                    pc.createOffer()
                        .then((offer) => {
                            if (isUnmounted) return;
                            return pc.setLocalDescription(offer);
                        })
                        .then(() => {
                            if (isUnmounted) return;
                            socket.emit("webrtc_signal", {
                                appoint_id,
                                sdp: pc.localDescription,
                                type: "offer"
                            });
                        });
                }
            })
            .catch((err) => {
                console.error("Failed to access microphone:", err);
                if (!isUnmounted) {
                    setStatus("Microphone access denied");
                }
            });

        pc.onicecandidate = (event) => {
            if (event.candidate) {
                socket.emit("webrtc_signal", {
                    appoint_id,
                    candidate: event.candidate,
                    type: "candidate"
                });
            }
        };

        pc.onconnectionstatechange = () => {
            if (pc.connectionState === "connected") {
                setStatus("Connected (WebRTC CoTURN)");
            } else if (pc.connectionState === "failed") {
                setStatus("Connection Failed");
            }
        };

        pc.ontrack = (event) => {
            if (remoteAudioRef.current) {
                remoteAudioRef.current.srcObject = event.streams[0];
            }
        };

        const handleSignal = (data) => {
            if (data.type === "offer" && pc.signalingState !== "stable") {
                pc.setRemoteDescription(new RTCSessionDescription(data.sdp))
                    .then(() => pc.createAnswer())
                    .then((answer) => pc.setLocalDescription(answer))
                    .then(() => {
                        socket.emit("webrtc_signal", {
                            appoint_id,
                            sdp: pc.localDescription,
                            type: "answer"
                        });
                    });
            } else if (data.type === "answer") {
                pc.setRemoteDescription(new RTCSessionDescription(data.sdp));
            } else if (data.type === "candidate" && data.candidate) {
                pc.addIceCandidate(new RTCIceCandidate(data.candidate))
                    .catch(err => console.error("Error adding ice candidate:", err));
            }
        };

        socket.on(`webrtc_signal_${appoint_id}`, handleSignal);

        return () => {
            isUnmounted = true;
            socket.off(`webrtc_signal_${appoint_id}`, handleSignal);
            if (localStreamRef.current) {
                try {
                    localStreamRef.current.getTracks().forEach(track => track.stop());
                } catch (e) {
                    console.error("Error stopping audio tracks in cleanup:", e);
                }
            }
            if (peerConnectionRef.current) {
                try {
                    peerConnectionRef.current.close();
                } catch (e) {
                    console.error("Error closing peer connection in cleanup:", e);
                }
            }
        };
    }, []);

    // Sync microphone toggle
    useEffect(() => {
        if (localStreamRef.current) {
            localStreamRef.current.getAudioTracks().forEach(track => {
                track.enabled = micOn;
            });
        }
    }, [micOn]);

    return (
        <div className="flex flex-col items-center">
            <audio ref={remoteAudioRef} autoPlay />
            <p className="text-gray-400 text-xs mt-2">{t(status)}</p>
            {/* Visual pulse indicator */}
            <div className="flex gap-1 justify-center items-center mt-3 h-[20px]">
                <span className="w-1 bg-green-500 rounded animate-bounce" style={{ height: '12px', animationDelay: '0.1s' }}></span>
                <span className="w-1 bg-green-500 rounded animate-bounce" style={{ height: '18px', animationDelay: '0.2s' }}></span>
                <span className="w-1 bg-green-500 rounded animate-bounce" style={{ height: '8px', animationDelay: '0.3s' }}></span>
                <span className="w-1 bg-green-500 rounded animate-bounce" style={{ height: '14px', animationDelay: '0.4s' }}></span>
            </div>
        </div>
    );
};

// ==========================================
// 3. Main Call Screen Component
// ==========================================
const Audiocall = ({ CallData, setIsVoiceCall }) => {
    const { t } = useTranslation();
    const {
        BaseUrl,
        agoraAppId,
        HandleCallApi,
        setIsToast,
        setToastMsg,
        setIsRating,
        setRatingData,
    } = useContext(MyContext);

    const [channel, setChannel] = useState("");
    const [calling, setCalling] = useState(false);
    const [micOn, setMic] = useState(true);
    const [chatTime, setChatTime] = useState("00:00");
    const [running, setRunning] = useState(false);
    const [apiCredentials, setApiCredentials] = useState(null);

    const dailyContainerRef = useRef(null);
    const [dailyCall, setDailyCall] = useState(null);

    const credentials = CallData?.credentials || apiCredentials;
    const provider = credentials?.provider || "agora";

    // Setup call on CallData
    useEffect(() => {
        if (!CallData) return;
        setChannel(`${CallData.astrologer_id}_${CallData.user_id}`);
        setCalling(true);
        getChatTime();
    }, [CallData]);

    // Timer interval
    useEffect(() => {
        if (!running) return;

        const interval = setInterval(() => {
            setChatTime((prev) => countdown(prev));
        }, 1000);

        return () => clearInterval(interval);
    }, [running]);

    useEffect(() => {
        if (calling && provider === "daily" && credentials?.roomUrl && dailyContainerRef.current) {
            if (dailyCall) {
                try { dailyCall.destroy(); } catch (e) {}
            }

            const callFrame = window.DailyIframe.createFrame(dailyContainerRef.current, {
                iframeStyle: {
                    width: '100%',
                    height: '100%',
                    border: 'none',
                    borderRadius: '15px'
                },
                showLeaveButton: true
            });

            callFrame.on('error', (e) => {
                console.error("Daily.co error event:", e);
                setIsToast(true);
                setToastMsg(t("Call connection error. Ending session..."));
                try { callFrame.destroy(); } catch (err) {}
                endCall(false);
            });

            callFrame.on('left-meeting', () => {
                try { callFrame.destroy(); } catch (err) {}
                endCall(true);
            });

            callFrame.join({ url: credentials.roomUrl }).catch(err => {
                console.error("Daily.co join failed:", err);
            });
            setDailyCall(callFrame);

            return () => {
                try { callFrame.destroy(); } catch (e) {}
            };
        }
    }, [calling, provider, credentials?.roomUrl]);

    const countdown = (time) => {
        const [m, s] = time.split(":").map(Number);
        let total = m * 60 + s - 1;
        if (total <= 0) return "00:00";
        return `${String(Math.floor(total / 60)).padStart(2, "0")}:${String(
            total % 60
        ).padStart(2, "0")}`;
    };

    const getChatTime = async () => {
        const res = await HandleCallApi("Post", "chat/audio_video_call", {
            appointment_id: CallData?.appoint_id,
        });
        setChatTime(res?.data?.chat_time?.chat_time_remaining || "00:00");
        const activeCreds = CallData?.credentials || res?.data?.credentials;
        const activeProvider = activeCreds?.provider || "agora";

        if (["daily", "mirotalk", "100ms", "janus"].includes(activeProvider) && !activeCreds?.roomUrl) {
            setIsToast(true);
            setToastMsg(t("Failed to load call room credentials. Ending session..."));
            endCall(false);
            return;
        }

        if (res?.data?.credentials) {
            setApiCredentials(res.data.credentials);
        }
        setRunning(true);
    };

    // Socket: call time over
    useEffect(() => {
        const userId = secureLocalStorage.getItem("Userid");

        const onChatTimeOver = () => {
            endCall(false);
            setMic(false);
            setCalling(false);
            setIsVoiceCall(false);
        };

        const handleIframeMessage = (event) => {
            if (event.data && event.data.type === 'end_call') {
                console.log("Received end_call message from iframe. Ending call...");
                endCall(true);
            }
        };

        socket.on(`chat_time_over_user${userId}`, onChatTimeOver);
        window.addEventListener('message', handleIframeMessage);

        return () => {
            socket.off(`chat_time_over_user${userId}`, onChatTimeOver);
            window.removeEventListener('message', handleIframeMessage);
        };
    }, [CallData]);

    const endCall = async (showRating = true) => {
        await HandleCallApi("Post", "astro/end_chat", {
            appoint_id: CallData?.appoint_id,
        });

        socket.emit("end_chat", { appoint_id: CallData?.appoint_id });

        setMic(false);
        setCalling(false);
        setIsVoiceCall(false);

        setIsToast(true);
        setToastMsg(t("Call ended"));

        if (showRating) {
            setIsRating(true);
            setRatingData({
                appointment_id: CallData?.appoint_id,
                astrologer_detail: {
                    image: CallData?.Astrologer_image,
                    name: CallData?.Astrologer_name,
                },
                astrologer_id: CallData?.astrologer_id,
                conversation_type: CallData?.appointment_type,
            });
        }
    };

    const toggleMic = () => {
        setMic(!micOn);
    };

    return (
        <div className="w-full h-full flex flex-col items-center justify-center bg-gray-900 rounded-[20px] p-6 text-white shadow-2xl relative overflow-hidden border border-gray-800">
            {/* Visual background gradient pulse */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[350px] h-[350px] bg-indigo-600/10 rounded-full blur-[80px] pointer-events-none animate-pulse"></div>

            {calling && (provider === "daily" || provider === "mirotalk" || provider === "100ms" || provider === "janus") ? (
                <div className="w-full h-full flex flex-col items-center justify-center z-10 relative">
                    <div className="w-full h-full rounded-[15px] overflow-hidden border border-gray-800 bg-black/40">
                        {provider === "daily" ? (
                            <div ref={dailyContainerRef} style={{ width: "100%", height: "100%" }} />
                        ) : (
                            <iframe
                                src={(provider === "mirotalk" || provider === "100ms" || provider === "janus") ? credentials?.roomUrl : `https://sandbox.100ms.live/meeting/${credentials?.roomId || "sandbox"}`}
                                allow="camera; microphone; autoplay; display-capture"
                                className="w-full h-full border-0"
                                title={t("Embedded Call Node")}
                            />
                        )}
                    </div>
                    <div className="mt-4 flex flex-col items-center">
                        <p className="text-sm font-medium text-indigo-400 capitalize">{provider} {t("Premium Engine")}</p>
                        <p className="text-gray-400 text-xs mt-1">{t("Time Remaining")}: {chatTime}</p>
                        <button
                            onClick={() => endCall(true)}
                            className="mt-4 bg-red-600 hover:bg-red-700 hover:scale-105 active:scale-95 duration-200 px-6 py-2.5 rounded-full font-semibold flex items-center gap-2 shadow-lg"
                        >
                            <svg className="fill-current w-5 h-5" viewBox="0 0 24 24"><path d="M16.5521 21.752c-.459 0-.921-.062-1.376-.187-6.165-1.692-11.046-6.57-12.739-12.731C2.051 7.433 2.262 5.973 3.032 4.725c.773-1.254 2.044-2.139 3.487-2.427.96-.192 1.909.236 2.396 1.057l1.563 2.64c.759 1.282.382 2.933-.859 3.759l-1.131.752c1.057 2.17 2.835 3.953 4.996 5.009l.762-1.137c.831-1.239 2.482-1.61 3.763-.847l2.643 1.576c.818.488 1.241 1.443 1.055 2.375-.288 1.443-1.173 2.714-2.426 3.487-.842.518-1.779.783-2.729.783z"/></svg>
                            {t("End Session")}
                        </button>
                    </div>
                </div>
            ) : (
                <div className="z-10 text-center w-full flex flex-col items-center justify-between h-full py-4">
                    {/* Header Details */}
                    <div>
                        <span className="bg-indigo-950/80 text-indigo-400 border border-indigo-900/60 px-4 py-1.5 rounded-full text-xs font-semibold uppercase tracking-wider">
                            {t("Voice Call")}
                        </span>
                        <h1 className="mt-4 text-[26px] font-bold text-white tracking-wide">
                            {CallData?.Astrologer_name}
                          </h1>
                        <p className="text-indigo-400 font-mono text-[20px] font-semibold mt-1 tracking-wider">{chatTime}</p>
                    </div>

                    {/* Astrologer Avatar with Pulse */}
                    <div className="relative my-8">
                        <div className="absolute inset-0 bg-indigo-500/20 rounded-full animate-ping scale-110"></div>
                        <div className="relative mx-auto w-[160px] h-[160px] rounded-full overflow-hidden border-[4px] border-indigo-500/40 shadow-2xl">
                            <img
                                src={BaseUrl + CallData?.Astrologer_image}
                                className="w-full h-full object-cover transform hover:scale-105 duration-300"
                                alt={t("Astrologer")}
                            />
                        </div>
                    </div>

                    {/* Conditional Engines (Agora / Native WebRTC / Janus) */}
                    {calling && (
                        provider === "agora" ? (
                            <AgoraAudioCall
                                CallData={CallData}
                                agoraAppId={credentials?.appId || agoraAppId}
                                token={credentials?.token}
                                channel={channel}
                                micOn={micOn}
                            />
                        ) : (
                            <WebRtcAudioCall
                                CallData={CallData}
                                credentials={credentials}
                                micOn={micOn}
                            />
                        )
                    )}

                    {/* Controls */}
                    <div className="flex gap-6 mt-8">
                        <button
                            onClick={toggleMic}
                            className={`p-4 rounded-full shadow-lg border duration-300 transform active:scale-95 ${
                                micOn 
                                ? "bg-gray-800/80 border-gray-700 hover:bg-gray-700 text-white" 
                                : "bg-red-500/20 border-red-500/40 hover:bg-red-500/30 text-red-400"
                            }`}
                        >
                            {micOn ? (
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3zm5.3-3c0 3-2.54 5.1-5.3 5.1S6.7 14 6.7 11H5c0 3.41 2.72 6.23 6 6.72V21h2v-3.28c3.28-.48 6-3.3 6-6.72h-1.7z" fill="currentColor" />
                                </svg>
                            ) : (
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M19 11h-1.7c0 .74-.16 1.43-.43 2.05l1.23 1.23c.56-.98.9-2.09.9-3.28zm-2.7 3.05l-1.2-1.2C15.7 12.08 16 11.57 16 11V5c0-2.21-1.79-4-4-4S8 2.79 8 5v1.88l8.3 8.3c.27-.63.43-1.32.43-2.13zm-10.6-9L4.27 3 3 4.27l6.01 6.01V11c0 2.21 1.79 4 4 4 .78 0 1.5-.23 2.11-.61l2.9 2.9C16.42 18.3 14.82 19 13 19v2h-2v-2.05c-3.79-.49-6.8-3.75-6.8-7.7H2.5c0 4.42 3.28 8.09 7.5 8.7V23h4v-1.3c1.78-.26 3.43-.99 4.79-2.09l1.89 1.89L24 20.22 5.7 1.95z" fill="currentColor" />
                                </svg>
                            )}
                        </button>

                        <button
                            onClick={() => endCall(true)}
                            className="bg-red-600 hover:bg-red-700 hover:scale-105 active:scale-95 duration-300 p-4 rounded-full shadow-lg border border-red-500/20 text-white"
                        >
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M16.5521 21.752C16.0931 21.752 15.6311 21.69 15.1751 21.565C9.01011 19.873 4.12909 14.995 2.43609 8.83402C2.05109 7.43302 2.26204 5.97301 3.03204 4.72501C3.80504 3.47101 5.0761 2.58601 6.5191 2.29801C7.4791 2.10601 8.4281 2.53401 8.9151 3.35501L10.4781 5.995C11.2371 7.277 10.86 8.928 9.61896 9.754L8.4881 10.506C9.5451 12.676 11.3229 14.459 13.4839 15.515L14.2462 14.378C15.0772 13.139 16.7281 12.768 18.0091 13.531L20.6522 15.107C21.4702 15.595 21.8931 16.55 21.7071 17.482C21.4191 18.925 20.5341 20.196 19.2811 20.969C18.4391 21.487 17.5021 21.752 16.5521 21.752Z" fill="currentColor" />
                            </svg>
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Audiocall;
